import React, { useContext, useEffect, useRef, useState } from "react";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import Search from "../../Assets/Images/search.png";
import Filter from "../../Assets/Images/filter.png";
import FilterDark from "../../Assets/Images/filter-dark.png";
import User from "../../Assets/Images/user.png";
import AllClientTable from "../AllClientTable/AllClientTable";
import { Link, useNavigate } from "react-router-dom";
import { Close } from "@mui/icons-material";
import AuthContext from "../../Context/AuthContext";
import { fetchAssessments, fetchDeployments, deleteDeployment } from "../../api";
import { CircularProgress, Button } from "@mui/material";
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from "react-datepicker";
import Notification from "../Dashboard/Notification/Notification";
import { toast } from "react-toastify";

export default function AllClient() {
  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [clientDataArray, setClientDataArray] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [memberRange, setMemberRange] = useState({ min: 0, max: 25 });
  const [maxAssessmentCount, setMaxAssessmentCount] = useState(25);
  const [value, setValue] = useState(0);
  const [openDatePicker, setOpenDatePicker] = useState(null); // 'start' or 'end' or null
  const authCtx = useContext(AuthContext);
  const [filters, setFilters] = useState({
    selectedIndustries: [],
    members: { min: 0, max: 25 },
    dateRange: { startDate: null, endDate: null },
    status: [],
  });
  const [searchValue, setSearchValue] = useState('');

  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const firstRenderRef = useRef(true);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [hasClients, setHasClients] = useState(false);

  const notificationRef = useRef(null);

  const navigate = useNavigate()

  const industries = [
    "Agriculture",
    "Automotive",
    "Construction",
    "Education/Training",
    "Energy/Utilities",
    "Engineering",
    "Entertainment/Media",
    "Finance/Banking",
    "Government/Public Services",
    "Healthcare/Medical",
    "Hospitality/Tourism",
    "Information Technology (IT)",
    "Insurance",
    "Leadership Development/Consulting",
    "Legal Services",
    "Manufacturing/Production",
    "Marketing/Advertising",
    "Non-Profit/Charity/Churches",
    "Real Estate/Property",
    "Transportation/Logistics"
  ];

  const statuses = ["done", "ongoing", "yet to start", "inactive"];

  useEffect(() => {
    setIsLoading(true);
    if (authCtx?.orgId || localStorage.getItem('orgId')) {
      fetchApi();
    } else {
      const timeout = setTimeout(() => {
        if (authCtx?.orgId || localStorage.getItem('orgId')) {
          fetchApi();
        } else {
          // console.log("No orgId available, stopping loading");
          setIsLoading(false);
        }
      }, 1000);

      return () => clearTimeout(timeout);
    }

    return () => {
      setIsLoading(false);
      setClientDataArray([]);
      setClientData([]);
      setHasClients(false);
    };
  }, [authCtx?.orgId]);

  useEffect(() => {
    if (!isLoading && !hasClients && (authCtx?.orgId || localStorage.getItem('orgId'))) {
      setIsLoading(true);
      fetchApi();
    }
  }, [authCtx?.orgId,]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading && !hasClients) {
        setIsLoading(false);
        setHasClients(false);
      }
    }, 15000);

    return () => clearTimeout(timeout);
  }, [isLoading, hasClients]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isNotificationOpen]);

  useEffect(() => {
    if (clientData.length > 0) {
      const maxCount = Math.max(...clientData.map(c => c.totalAssessments || 0));
      setMaxAssessmentCount(maxCount > 0 ? maxCount : 25);
      setMemberRange((prev) => ({ ...prev, max: maxCount > 0 ? maxCount : 25 }));

      setFilters((prev) => ({
        ...prev,
        members: { ...prev.members, max: maxCount > 0 ? maxCount : 25 }
      }));
    }
  }, [clientData]);

  const fetchApi = async () => {
    setIsLoading(true)
    try {
      const clientData = await fetchDeployments(authCtx)
      // console.log("Fetched clientData:", clientData)
      if (clientData?.length > 0) {
        setClientDataArray(clientData)
        setClientData(clientData)
        setHasClients(true)

        // Fetch assessments for each client
        let clientDatawithAssessments = await Promise.all(clientData.map(async (client) => {
          try {
            let assessments = await fetchAssessments(client.id)
            let completeAssessments = assessments.filter(a => a.status === "complete")
            // console.log("completeAssessments", completeAssessments)

            let totalMembers = assessments.reduce((count, assessment) => {
              return count + (assessment.members ? assessment.members.length : 0);
            }, 0);

            return {
              ...client,
              totalAssessments: assessments?.length > 0 ? assessments.length : 0,
              completeAssessments: completeAssessments.length,
              pendingAssessment: (assessments?.length > 0 ? assessments.length : 0) - completeAssessments.length,
              assessments,
              totalMembers,
            }
          } catch (assessmentError) {
            // console.error("Error fetching assessments for client:", client.id, assessmentError)
            // Return client data without assessments if fetch fails
            return {
              ...client,
              totalAssessments: 0,
              completeAssessments: 0,
              pendingAssessment: 0,
              assessments: [],
              totalMembers: 0,
            }
          }
        }))
        setClientDataArray(clientDatawithAssessments)
        setClientData(clientDatawithAssessments)
      } else {
        setHasClients(false)
      }
    }
    catch (error) {
      // console.error("Error fetching client data:", error)
      setHasClients(false)
    }
    finally {
      setIsLoading(false)
    }
  }

  const toggleFilter = () => {
    setFilterOpen((prevState) => !prevState);
  };

  const handleIndustryChange = (event) => {
    const { value, checked } = event.target;

    setFilters((prevFilters) => {
      const updatedIndustries = checked
        ? [...prevFilters.selectedIndustries, value]
        : prevFilters.selectedIndustries.filter((industry) => industry !== value);

      // console.log("Updated Industries:", updatedIndustries);
      return { ...prevFilters, selectedIndustries: updatedIndustries };
    });
  };

  const handleMemberRangeChange = (event) => {
    const value = Number(event.target.value);
    setMemberRange((prev) => ({ ...prev, max: value }));

    setFilters((prevFilters) => ({
      ...prevFilters,
      members: { ...prevFilters.members, max: value },
    }));
  };

  const handleStatusChange = (event) => {
    const { value, checked } = event.target;
    setFilters((prevFilters) => {
      const updatedStatus = checked
        ? [...prevFilters.status, value]
        : prevFilters.status.filter((status) => status !== value);

      return { ...prevFilters, status: updatedStatus };
    });
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        startDate: date, // Store as a Date object
      },
    }));
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        endDate: date
      }
    }));
  };
  //   setEndDate(date);
  //   setFilters((prevFilters) => ({
  //     ...prevFilters,
  //     dateRange: {
  //       ...prevFilters.dateRange,
  //       endDate: date, // Store as a Date object
  //     },
  //   }));
  // };

  const handleSearch = (e) => {
    const searchInput = e?.target?.value?.toLowerCase()?.trim();
    setSearchValue(searchInput);
  }

  useEffect(() => {
    let filteredClients = [...clientData];
    if (searchValue && searchValue.length > 0) {
      filteredClients = filteredClients.filter(item => {
        return item?.name && item.name.toLowerCase().includes(searchValue);
      });
    }

    if (filters.selectedIndustries.length > 0) {
      filteredClients = filteredClients.filter((client) =>
        filters.selectedIndustries.includes(client?.industry?.toLowerCase())
      );
    }

    // if (filters.members) {
    //   filteredClients = filteredClients.filter((client) => {
    //     const members = client.members ?? 0;
    //     return members >= filters.members.min && members <= filters.members.max;
    //   });
    // }

    // console.log(filteredClients, 'filteredClients')

    if (filters.members) {
      filteredClients = filteredClients.filter(
        (client) =>
          client.totalAssessments >= filters.members.min &&
          client.totalAssessments <= filters.members.max
      );
    }

    // console.log("filters", filters)

    if (filters.status.length > 0) {
      filteredClients = filteredClients.filter(client =>
        filters.status?.some(status => {
          switch (status) {
            case "done":
              return client.assessments.some(assessment => assessment.status === "complete")
            case "ongoing":
              return client.assessments.some(assessment => assessment.status === "pending");
            case "yet to start":
              return client.assessments.some(assessment => assessment.status === "active")
            case "inactive":
              return client.assessments.every(assessment => assessment.status === "inactive");
            default:
              return false;
          }
        })
      );
    }

    const { startDate, endDate } = filters.dateRange || {};

    if (startDate || endDate) {
      const selectedStartDate = startDate ? new Date(startDate) : null;
      const selectedEndDate = endDate ? new Date(endDate) : null;

      if (selectedStartDate) selectedStartDate.setHours(0, 0, 0, 0);
      if (selectedEndDate) selectedEndDate.setHours(23, 59, 59, 999);

      filteredClients = filteredClients.filter((client) => {
        if (!client.started) return false;

        const clientDate = new Date(client.started);

        if (selectedStartDate && clientDate < selectedStartDate) return false;
        if (selectedEndDate && clientDate > selectedEndDate) return false;

        return true;
      });
    }


    const noFiltersApplied =
      !filters.selectedIndustries?.length &&
      !filters.status?.length &&
      !filters.dateRange?.startDate &&
      !filters.dateRange?.endDate &&
      filters.members.min === 0 &&
      filters.members.max === maxAssessmentCount &&
      !searchValue;

    const newDataArray = noFiltersApplied ? clientData : filteredClients;

    if (JSON.stringify(newDataArray) !== JSON.stringify(clientDataArray)) {
      setClientDataArray(newDataArray);
    }

    // console.log("After Status Filter:", filters);
  }, [filters, clientData, searchValue]);

  // console.log("clientDataArray", clientDataArray)

  const handleNotificationOpen = () => {
    setIsNotificationOpen(true);
  }
  const handleNotificationClose = () => {
    setIsNotificationOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      selectedIndustries: [],
      members: { min: 0, max: maxAssessmentCount },
      dateRange: { startDate: null, endDate: null },
      status: [],
    });
    setStartDate("");
    setEndDate("");
    setMemberRange({ min: 0, max: maxAssessmentCount });
    setSearchValue("");
    setClientDataArray(clientData);
  };

  const addMoreClient = (e) => {
    e.preventDefault()
    navigate('/add-client')
  }

  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, loading }

  const openDeleteModal = (deploymentId, clientName) => {
    setDeleteTarget({
      id: deploymentId,
      name: clientName,
      loading: false,
    });
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const handleDeleteDeployment = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteTarget(prev => ({ ...prev, loading: true }));
      const orgId = authCtx?.orgId || localStorage.getItem('orgId');
      await deleteDeployment({ orgId, deploymentId: deleteTarget.id });
      setClientDataArray((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setClientData((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      closeDeleteModal();
      toast.success("Client deleted successfully!");
    } catch (error) {
      console.error('Error deleting deployment:', error);
      // Optional: integrate with existing notification/toast system instead of alert
      window.alert(error?.message || 'Failed to delete client. Please try again.');
      setDeleteTarget(prev => ({ ...prev, loading: false }));
    }
  };


  // console.log('clientDataArray', clientDataArray)

  return (
    <div className="dashboard-platform">
      <DashboardHeader heading='Clients' handleNotificationOpen={handleNotificationOpen} />
      <Notification ref={notificationRef} handleNotificationClose={handleNotificationClose} isNotificationOpen={isNotificationOpen} />
      {isLoading ?
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </div> :
        !hasClients ?
          <div className="addassessmentdetails">
            <p className="pform">Looks like your client list is empty.</p>
            <p className="pform">Let's get started by adding your first one!</p>
            <Button className="cta-assessment" onClick={(e) => addMoreClient(e)}>
              Add Client
            </Button>
          </div>
          :
          clientDataArray && clientDataArray.length === 0 ?
            <div className="addassessmentdetails">
              <p className="pform">We couldn't find anything that fits this filter.</p>
              <p className="pform">Try a different selection to explore results.</p>
            </div>
            :
            <>
              <div className="allclient-container">

                <div className="container-topbar">
                  <div className="search-container">
                    <div className="input-wrapper">
                      <img src={Search} alt="search Icon" className="search" />
                      <input type="text" placeholder="Search" className="search-input" value={searchValue} onChange={handleSearch} />
                    </div>
                    <div className="filter-section" onClick={toggleFilter}>
                      <img src={filterOpen ? FilterDark : Filter} alt="filter Icon" className="filter" />
                      <span className="filter-text">Filter</span>
                      {/* <span className={`filter-text ${filterOpen ? 'bold' : ''}`}>Filter</span> */}
                    </div>
                  </div>
                  <div className="logo-container" style={{ textDecoration: "none" }} onClick={(e) => addMoreClient(e)} >
                    <span className="plus-icon">+</span>
                    <img src={User} alt="user Icon" className="user" />
                  </div>
                </div>
              </div>
              <div className="table-container">
                <AllClientTable data={clientDataArray} isLoading={isLoading} onDelete={openDeleteModal} />
              </div>

            </>
      }
      {filterOpen && (
        <div className="filter-popup">
          <div className="filter-header">
            <span className="filter-title">Filters</span>
            <div className="close-section">
              <div className="filter-actions">
                <button className="clear-button" onClick={handleClearFilters}> Clear Filter </button>
              </div>
              <button className="close-button" onClick={toggleFilter}> <Close /> </button>
            </div>
          </div>

          <hr />
          <div>
            <div className="filter-section">
              <label className="filter-label">Industry</label>
              <div className="checkbox-section">
                {industries.map((industry, index) => (
                  <label key={index} className="checkbox-lable">
                    <input type="checkbox" className="checkbox" value={industry.toLowerCase()} checked={filters.selectedIndustries.includes(industry.toLowerCase())} onChange={handleIndustryChange} />
                    {" "} {industry}
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <label className="filter-label">No of Assessments</label>
              <div className="range-section">
                <div className="range-values">
                  <span>{memberRange.min}</span>
                  <span>{memberRange.max}</span>
                </div>
                <div className="range-input">
                  <input
                    type="range"
                    min="0"
                    max={maxAssessmentCount}
                    value={memberRange.max}
                    onChange={handleMemberRangeChange}
                    style={{
                      background: `linear-gradient(to right, rgba(191, 149, 64, 1) ${(memberRange.max / maxAssessmentCount) * 100}%, rgba(199, 199, 199, 1) ${(memberRange.max / maxAssessmentCount) * 100}%)`,
                    }}
                    className="members-range"
                  />
                </div>
              </div>
            </div>

            <div className="filter-status">
              <label className="filter-label">Status</label>
              <div className="checkbox-group">
                {statuses.map((status) => (
                  <label key={status} className="checkbox-group-label">
                    <input type="checkbox" className="checkbox" value={status} checked={filters.status.includes(status)} onChange={handleStatusChange} />
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <label className="filter-label">Date</label>
              <div className="date-container">
                <div className="input-wrapper">
                  <DatePicker
                    selected={filters.dateRange.startDate}
                    onChange={handleStartDateChange}
                    onCalendarOpen={() => setOpenDatePicker('start')}
                    onCalendarClose={() => setOpenDatePicker(null)}
                    open={openDatePicker === 'start'}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Start Date"
                    className="date-picker-input"
                    onClickOutside={() => setOpenDatePicker(null)}
                  />
                </div>
                <div className="date-separator">
                  <span>to</span>
                </div>
                <div className="input-wrapper">
                  <DatePicker
                    selected={filters.dateRange.endDate}
                    onChange={handleEndDateChange}
                    onCalendarOpen={() => setOpenDatePicker('end')}
                    onCalendarClose={() => setOpenDatePicker(null)}
                    open={openDatePicker === 'end'}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="End Date"
                    className="date-picker-input"
                    onClickOutside={() => setOpenDatePicker(null)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && (
        <div className="client-delete-overlay">
          <div className="client-delete-modal">
            <div className="client-delete-icon-wrap">
              <svg width="64" height="64" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" className="client-delete-icon">
                <rect width="38" height="38" rx="19" fill="#B6111F"/>
                <path d="M16.2375 24L19 21.1111L21.7625 24L23.25 22.4444L20.4875 19.5556L23.25 16.6667L21.7625 15.1111L19 18L16.2375 15.1111L14.75 16.6667L17.5125 19.5556L14.75 22.4444L16.2375 24ZM13.6875 29C13.1031 29 12.6029 28.7824 12.1867 28.3472C11.7706 27.912 11.5625 27.3889 11.5625 26.7778V12.3333H10.5V10.1111H15.8125V9H22.1875V10.1111H27.5V12.3333H26.4375V26.7778C26.4375 27.3889 26.2294 27.912 25.8133 28.3472C25.3971 28.7824 24.8969 29 24.3125 29H13.6875ZM24.3125 12.3333H13.6875V26.7778H24.3125V12.3333Z" fill="white"/>
              </svg>
            </div>
            <h2 className="client-delete-text">
              Are you sure you want to delete this client?
            </h2>
            <p className="client-delete-sub-text">
              This will permanently delete all associated assessments and reports.
            </p>
            <p className="client-delete-sub-text">
              Please download any reports you wish to keep.
            </p>
            <div className="client-delete-actions">
              <Button
                className="client-delete-cancel"
                variant="contained"
                onClick={closeDeleteModal}
                disabled={deleteTarget.loading}
              >
                No, Cancel
              </Button>
              <Button
                className="client-delete-confirm"
                variant="outlined"
                onClick={handleDeleteDeployment}
                disabled={deleteTarget.loading}
              >
                {deleteTarget.loading ? "Deleting..." : "Yes, Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
